import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Shield, Key } from "lucide-react";
import { TopBar } from "@/components/layout";
import ExpiryAlertBanner from "@/components/ExpiryAlertBanner";
import { useCmsBlocks } from "@/hooks/useCmsBlocks";
import { useInstructions } from "@/hooks/useInstructions";
import { CmsAdminPanel } from "@/components/CmsAdminPanel";
import AuthCodePanel from "@/components/AuthCodePanel";
import SenutoTokenPanel from "@/components/SenutoTokenPanel";
import InstructionModal from "@/components/InstructionModal";
import { DomainErrorsCard } from "@/components/dashboard/DomainErrorsCard";
import { MapsStatsCard } from "@/components/dashboard/MapsStatsCard";

const Dashboard = () => {
  const navigate = useNavigate();
  const { blocks, loading: blocksLoading, isAdmin } = useCmsBlocks();
  const { getInstructionById } = useInstructions();

  const [showAuthCodePanel, setShowAuthCodePanel] = React.useState(false);
  const [showSenutoTokenPanel, setShowSenutoTokenPanel] = React.useState(false);
  const [showInstructionModal, setShowInstructionModal] = React.useState(false);
  const [selectedInstruction, setSelectedInstruction] = React.useState<any>(null);

  const handleCmsBlockClick = (block: any) => {
    if (block.target_url) {
      if (block.target_url.startsWith("http")) {
        window.open(block.target_url, "_blank");
      } else {
        navigate(block.target_url);
      }
    }
  };

  const handleShowInstruction = (instructionId: string) => {
    const instruction = getInstructionById(instructionId);
    if (instruction) {
      setSelectedInstruction(instruction);
      setShowInstructionModal(true);
    }
  };

  return (
    <>
      <TopBar title="Panel glowny" />
      <div className="flex-1 overflow-auto p-6 space-y-6">
        {/* Expiry Alerts */}
        <ExpiryAlertBanner />

        {/* Bledy wykonan (7 dni) */}
        <DomainErrorsCard />

        {/* Statystyki map — full width */}
        <MapsStatsCard />

        {/* CMS Blocks - Narzedzia */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Narzedzia</h2>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAuthCodePanel(true)}
              >
                <Shield className="h-4 w-4 mr-1" />
                Kody auth
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSenutoTokenPanel(true)}
              >
                <Key className="h-4 w-4 mr-1" />
                Senuto token
              </Button>
            </div>
          </div>

          {blocksLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardHeader>
                    <div className="h-4 bg-muted rounded w-3/4" />
                    <div className="h-3 bg-muted rounded w-1/2" />
                  </CardHeader>
                  <CardContent>
                    <div className="h-12 bg-muted rounded" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : blocks.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {blocks.map((block) => (
                <Card
                  key={block.id}
                  className="transition-all duration-200 hover:shadow-md cursor-pointer"
                  onClick={() => handleCmsBlockClick(block)}
                >
                  <CardHeader className="space-y-2">
                    <div className="flex items-center space-x-3">
                      <div
                        className={`p-2 rounded-lg ${block.color} text-white text-xl`}
                      >
                        {block.emoji}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-sm">
                            {block.title}
                          </CardTitle>
                          {block.instruction_id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShowInstruction(block.instruction_id);
                              }}
                              className="h-7 text-xs"
                            >
                              Instrukcja
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-xs leading-relaxed">
                      {block.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Brak dodanych narzedzi. Uzyj panelu admina ponizej aby dodac.
            </p>
          )}
        </div>

        {/* Admin CMS Panel */}
        {isAdmin && <CmsAdminPanel className="mt-2" />}
      </div>

      {/* Auth Code Modal */}
      <Dialog open={showAuthCodePanel} onOpenChange={setShowAuthCodePanel}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-orange-500" />
              Generator Kodow Autoryzacyjnych
            </DialogTitle>
          </DialogHeader>
          <AuthCodePanel />
        </DialogContent>
      </Dialog>

      {/* Senuto Token Modal */}
      <Dialog
        open={showSenutoTokenPanel}
        onOpenChange={setShowSenutoTokenPanel}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-indigo-500" />
              Senuto API Token
            </DialogTitle>
          </DialogHeader>
          <SenutoTokenPanel />
        </DialogContent>
      </Dialog>

      {/* Instruction Modal */}
      <InstructionModal
        instruction={selectedInstruction}
        isOpen={showInstructionModal}
        onClose={() => setShowInstructionModal(false)}
      />
    </>
  );
};

export default Dashboard;
